/**
 * 個別説明「実施状況」タブの CSV / Excel 書き出し (2026-10-07)。
 * 顧客リスト (contactsExport.ts)・フォーム回収一覧と同じ方式:
 * 絞り込み後の表示中の行をそのまま書き出す。CSV は UTF-8 BOM 付き、Excel は列幅自動調整。
 */
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import {
  BRIEFING_BILLING_LABEL,
  BRIEFING_ROW_STATE_LABEL,
  type BriefingStatusRow,
} from './briefingStatus';

const HEADERS = [
  '申込日',
  '予約日',
  '記録日',
  '氏名',
  '商談タイトル',
  '担当SA',
  '状態',
  'SA請求',
  '請求番号',
] as const;

function fmt(d: Date | null): string {
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export interface ExportResolvers {
  contactName: (contactId: string | null) => string;
  saName: (uid: string | null) => string;
}

function buildTable(rows: BriefingStatusRow[], r: ExportResolvers): string[][] {
  return rows.map((row) => {
    const b = row.billing;
    const billing =
      b.kind === 'none'
        ? ''
        : b.kind === 'not_applicable'
          ? `${BRIEFING_BILLING_LABEL.not_applicable}（${b.reason ?? ''}）`
          : BRIEFING_BILLING_LABEL[b.kind];
    return [
      fmt(row.appliedAt),
      fmt(row.scheduledAt),
      fmt(row.recordedAt),
      r.contactName(row.contactId),
      row.dealTitle,
      r.saName(row.saUid),
      BRIEFING_ROW_STATE_LABEL[row.state],
      billing,
      b.invoiceNumber ?? '',
    ];
  });
}

function exportFileStamp(): string {
  return new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** ファイル名の識別部分 (例: 2026-08 / SA-2026-09-001) */
function safeLabel(label: string): string {
  return label.replace(/[\s/\\?%*:|"<>]/g, '_').slice(0, 40);
}

export function exportBriefingStatusCsv(
  rows: BriefingStatusRow[],
  resolvers: ExportResolvers,
  label: string,
) {
  const csv = Papa.unparse({ fields: [...HEADERS], data: buildTable(rows, resolvers) });
  // Excel での文字化け防止のため UTF-8 BOM を付与
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `briefing_status_${safeLabel(label)}_${exportFileStamp()}.csv`);
}

export function exportBriefingStatusXlsx(
  rows: BriefingStatusRow[],
  resolvers: ExportResolvers,
  label: string,
) {
  const data = buildTable(rows, resolvers);
  const headers = [...HEADERS];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  // 列幅を内容に応じて自動調整（10〜60 文字）
  ws['!cols'] = headers.map((h, ci) => {
    const maxLen = data.reduce((m, r) => Math.max(m, String(r[ci] ?? '').length), h.length);
    return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '実施状況');
  XLSX.writeFile(wb, `briefing_status_${safeLabel(label)}_${exportFileStamp()}.xlsx`);
}
